import { ApiService } from './api-services/ApiService.js';
import { socketService } from './unifiedSocketService.js';
import authService from './authService.js';
import {
  SOCKET_EVENTS,
  UserStatusChangedPayload,
  DoctorAvailabilityChangedPayload,
  SocketEventName,
} from '../constants/socketEvents.js';
import { logger } from '../utils/logger.js';
import { userStatusSchema, presenceStatsSchema } from '../schemas/apiSchemas.js';

export interface UserStatus {
  userId: string;
  status: 'ONLINE' | 'OFFLINE' | 'AWAY';
  availabilityStatus?: 'AVAILABLE' | 'BUSY' | 'OFFLINE';
  lastSeen: Date;
  lastHeartbeat: Date;
  currentActivity?: string;
}

export interface PresenceStats {
  totalOnline: number;
  totalDoctorsAvailable: number;
  totalPatients: number;
  totalDoctors: number;
  totalAdmins: number;
}

type PresenceRole = 'DOCTOR' | 'PATIENT' | 'ADMIN';

type PresenceApiUser = {
  id?: number | string;
  userId?: number | string;
  name?: string;
  email?: string;
  role?: PresenceRole;
  status?: 'ONLINE' | 'OFFLINE' | 'AWAY';
  presenceStatus?: 'ONLINE' | 'OFFLINE' | 'AWAY';
  availabilityStatus?: 'AVAILABLE' | 'BUSY' | 'OFFLINE';
  lastSeen?: string | Date;
  lastHeartbeat?: string | Date;
  currentActivity?: string;
};

export class UserStatusService {
  private static instance: UserStatusService;
  private listeners: Map<SocketEventName, (data: unknown) => void> = new Map();
  private heartbeatTimeoutMs = 120000;
  private awayTimeoutMs = 300000;
  private configLoaded = false;
  private configPromise: Promise<void> | null = null;

  static getInstance(): UserStatusService {
    if (!UserStatusService.instance) {
      UserStatusService.instance = new UserStatusService();
    }
    return UserStatusService.instance;
  }

  private async refreshPresenceConfig(force = false): Promise<void> {
    if (!force) {
      if (this.configLoaded) {
        return;
      }
      if (this.configPromise) {
        return this.configPromise;
      }
    }

    this.configPromise = (async () => {
      try {
        const response = await ApiService.getInstance().getNorm<{
          heartbeatTimeoutMs: number;
          awayTimeoutMs: number;
        }>('/api/user-status/config');
        const data = response.data;
        if (data?.heartbeatTimeoutMs) {
          this.heartbeatTimeoutMs = data.heartbeatTimeoutMs;
        }
        if (data?.awayTimeoutMs) {
          this.awayTimeoutMs = data.awayTimeoutMs;
        }
        this.configLoaded = true;
        logger.info('[UserStatus] Configuration présence chargée', data);
      } catch (error) {
        logger.warn(
          '[UserStatus] Impossible de charger la configuration présence, valeurs par défaut utilisées',
          error
        );
      }
    })();

    try {
      await this.configPromise;
    } finally {
      this.configPromise = null;
    }
  }

  async ensurePresenceConfig(options?: { force?: boolean }): Promise<void> {
    await this.refreshPresenceConfig(options?.force ?? false);
  }

  getHeartbeatTimeoutMs(): number {
    return this.heartbeatTimeoutMs;
  }

  getAwayTimeoutMs(): number {
    return this.awayTimeoutMs;
  }

  /**
   * Marquer l'utilisateur comme en ligne
   */
  async setUserOnline(role?: PresenceRole): Promise<void> {
    try {
      const token = authService.getToken();
      if (!token) {
        logger.warn('[UserStatus] Impossible de marquer en ligne : token indisponible');
        return;
      }

      await this.refreshPresenceConfig();

      const payload: Record<string, unknown> = {};
      if (role) {
        payload.role = role;
      }

      try {
        await ApiService.getInstance().postNorm('/api/user-status/online', payload);
        logger.info('[UserStatus] Utilisateur marqué en ligne');
      } catch {
        // Fallback: utiliser l'endpoint auth/verify pour maintenir la session
        await ApiService.getInstance().get('/api/auth/me');
        logger.info('[UserStatus] Mode dégradé - session maintenue via auth/verify');
      }

      // Envoyer un heartbeat initial (le flux temp réel socket gérera le reste)
      // On attend un court instant pour laisser le temps aux cookies CSRF de se stabiliser après auth/me
      setTimeout(() => {
        this.sendHeartbeat('initial_login');
      }, 2000);
    } catch (error) {
      logger.error('[UserStatus] Erreur setUserOnline', error);
    }
  }

  /**
   * Marquer l'utilisateur comme hors ligne
   */
  async setUserOffline(role?: PresenceRole): Promise<void> {
    try {
      try {
        const payload: Record<string, unknown> = {};
        if (role) {
          payload.role = role;
        }
        await ApiService.getInstance().postNorm('/api/user-status/offline', payload);
        logger.info('[UserStatus] Utilisateur marqué hors ligne');
      } catch {
        // Fallback: endpoint non implémenté, continuer sans erreur
        logger.info('[UserStatus] Mode dégradé - endpoint offline non implémenté');
      }

      // Déconnexion manuelle effectuée
      logger.info('[UserStatus] Statut offline consolidé');
    } catch (error) {
      logger.error('[UserStatus] Erreur setUserOffline', error);
    }
  }

  /**
   * Envoyer un heartbeat
   */
  async sendHeartbeat(activity?: string): Promise<void> {
    try {
      await ApiService.getInstance().postNorm('/api/user-status/heartbeat', { activity });
    } catch {
      // Fallback: utiliser auth/verify pour maintenir la session
      try {
        await ApiService.getInstance().get('/api/auth/me');
      } catch {
        // Ignorer les erreurs silencieusement
      }
    }
  }

  /**
   * Obtenir le statut d'un utilisateur
   */
  async getUserStatus(userId: string): Promise<UserStatus | null> {
    try {
      const response = await ApiService.getInstance().postNorm<Record<string, UserStatus>>(
        '/api/user-status/bulk',
        { userIds: [userId] }
      );

      const rawPayload = response.data ?? {};
      const statusMap: Record<string, UserStatus> =
        (rawPayload as { data?: Record<string, UserStatus> }).data ?? rawPayload;
      const rawStatus = statusMap[userId];

      if (!rawStatus) {
        return null;
      }

      const parsed = userStatusSchema.safeParse(rawStatus);
      if (parsed.success) {
        return parsed.data as UserStatus;
      }

      logger.warn('[UserStatus] Données invalides pour getUserStatus', { userId, rawStatus });
      return null;
    } catch (error) {
      logger.error('[UserStatus] Erreur getUserStatus', error);
      return null;
    }
  }

  /**
   * Obtenir les statuts de plusieurs utilisateurs
   */
  async getUsersStatus(userIds: string[]): Promise<Record<string, UserStatus>> {
    try {
      const response = await ApiService.getInstance().postNorm<Record<string, UserStatus>>(
        '/api/user-status/bulk',
        { userIds }
      );
      return response.data || {};
    } catch (error) {
      logger.error('[UserStatus] Erreur getUsersStatus', error);
      return {};
    }
  }

  /**
   * Obtenir tous les utilisateurs en ligne
   */
  async getOnlineUsers(): Promise<UserStatus[]> {
    try {
      const [doctors, patients] = await Promise.all([this.getDoctors(), this.getPatients()]);
      return [...doctors, ...patients] as UserStatus[];
    } catch (error) {
      logger.error('[UserStatus] Erreur getOnlineUsers', error);
      return [];
    }
  }

  /**
   * Obtenir les médecins (présence temps réel)
   * Wrapper pratique autour de getOnlineUsers() pour filtrer par rôle.
   */
  async getDoctors(): Promise<(UserStatus & Record<string, unknown>)[]> {
    try {
      const response = await ApiService.getInstance().getNorm<
        PresenceApiUser[] | { data?: PresenceApiUser[] }
      >('/api/users/doctors');
      const rawUsers = Array.isArray(response.data)
        ? response.data
        : Array.isArray((response.data as { data?: PresenceApiUser[] } | undefined)?.data)
          ? ((response.data as { data?: PresenceApiUser[] }).data ?? [])
          : [];

      return rawUsers
        .map((user) => this.normalizePresenceUser(user, 'DOCTOR'))
        .filter((user): user is UserStatus & Record<string, unknown> => user !== null);
    } catch (error) {
      logger.error('[UserStatus] Erreur getDoctors', error);
      return [];
    }
  }

  /**
   * Obtenir les patients (présence temps réel)
   */
  async getPatients(): Promise<(UserStatus & Record<string, unknown>)[]> {
    try {
      const response = await ApiService.getInstance().getNorm<
        PresenceApiUser[] | { data?: PresenceApiUser[] }
      >('/api/users/online');
      const rawUsers = Array.isArray(response.data)
        ? response.data
        : Array.isArray((response.data as { data?: PresenceApiUser[] } | undefined)?.data)
          ? ((response.data as { data?: PresenceApiUser[] }).data ?? [])
          : [];

      return rawUsers
        .filter((user) => (user.role ?? 'PATIENT') === 'PATIENT')
        .map((user) => this.normalizePresenceUser(user, 'PATIENT'))
        .filter((user): user is UserStatus & Record<string, unknown> => user !== null);
    } catch (error) {
      logger.error('[UserStatus] Erreur getPatients', error);
      return [];
    }
  }

  /**
   * Obtenir les médecins disponibles
   */
  async getAvailableDoctors(): Promise<UserStatus[]> {
    try {
      const response = await ApiService.getInstance().getNorm<UserStatus[]>(
        '/api/user-status/doctors/available'
      );
      const rawData = response.data;
      const arr = Array.isArray(rawData) ? rawData : [];
      const filtered = arr.filter((u): u is UserStatus => userStatusSchema.safeParse(u).success);
      return filtered;
    } catch (error) {
      logger.error('[UserStatus] Erreur getAvailableDoctors', error);
      return [];
    }
  }

  /**
   * Mettre à jour le statut de disponibilité (médecin)
   */
  async updateAvailabilityStatus(
    availabilityStatus: 'AVAILABLE' | 'BUSY' | 'OFFLINE'
  ): Promise<void> {
    try {
      await ApiService.getInstance().putNorm('/api/user-status/availability', {
        availabilityStatus,
      });
      logger.info('[UserStatus] Disponibilité mise à jour', availabilityStatus);
    } catch (error) {
      logger.error('[UserStatus] Erreur updateAvailabilityStatus', error);
      throw error;
    }
  }

  /**
   * Obtenir les statistiques de présence
   */
  async getPresenceStats(): Promise<PresenceStats> {
    try {
      const response = await ApiService.getInstance().getNorm<PresenceStats>(
        '/api/user-status/stats/presence'
      );
      const parsed = response.data ? presenceStatsSchema.safeParse(response.data) : null;
      if (parsed && parsed.success) return parsed.data as PresenceStats;
      return {
        totalOnline: 0,
        totalDoctorsAvailable: 0,
        totalPatients: 0,
        totalDoctors: 0,
        totalAdmins: 0,
      };
    } catch (error) {
      logger.error('[UserStatus] Erreur getPresenceStats', error);
      return {
        totalOnline: 0,
        totalDoctorsAvailable: 0,
        totalPatients: 0,
        totalDoctors: 0,
        totalAdmins: 0,
      };
    }
  }

  /**
   * Écouter les changements de statut
   */
  onStatusChanged(callback: (data: UserStatusChangedPayload) => void): void {
    const handler = (data: unknown) => callback(data as UserStatusChangedPayload);
    this.listeners.set(SOCKET_EVENTS.USER_STATUS_CHANGED, handler);
    socketService.on(SOCKET_EVENTS.USER_STATUS_CHANGED, handler);
  }

  /**
   * Écouter les changements de disponibilité
   */
  onAvailabilityChanged(callback: (data: DoctorAvailabilityChangedPayload) => void): void {
    const handler = (data: unknown) => callback(data as DoctorAvailabilityChangedPayload);
    this.listeners.set(SOCKET_EVENTS.DOCTOR_AVAILABILITY_CHANGED, handler);
    socketService.on(SOCKET_EVENTS.DOCTOR_AVAILABILITY_CHANGED, handler);
  }

  /**
   * Nettoyer les listeners
   */
  cleanup(): void {
    this.listeners.forEach((listener, event) => {
      socketService.off(event, listener);
    });
    this.listeners.clear();
    logger.info('[UserStatus] Cleanup effectué');
  }

  private normalizePresenceUser(
    user: PresenceApiUser,
    fallbackRole: PresenceRole
  ): (UserStatus & Record<string, unknown>) | null {
    const resolvedUserId = user.userId ?? user.id;

    if (!resolvedUserId) {
      return null;
    }

    const normalized = {
      ...user,
      userId: String(resolvedUserId),
      status: user.status ?? user.presenceStatus ?? 'ONLINE',
      availabilityStatus:
        user.availabilityStatus ?? (fallbackRole === 'DOCTOR' ? 'AVAILABLE' : undefined),
      lastSeen: user.lastSeen ? new Date(user.lastSeen) : new Date(),
      lastHeartbeat: user.lastHeartbeat ? new Date(user.lastHeartbeat) : new Date(),
      role: user.role ?? fallbackRole,
    };

    const parsed = userStatusSchema.safeParse(normalized);
    if (parsed.success) {
      return {
        ...(normalized as Record<string, unknown>),
        ...parsed.data
      } as UserStatus & Record<string, unknown>;
    }

    return normalized as unknown as UserStatus & Record<string, unknown>;
  }
}

export const userStatusService = UserStatusService.getInstance();
export default userStatusService;
